import React from "react";
import { makeStyles, Theme, UIBox } from "../../_core/components";
import UITypography from "../../_core/components/ui/UITypography";
import UIModalDialog from "../../_core/components/ui/UIModalDialog";
import { PartnerUser } from "../api/types";
import { Warning as WarningIcon } from "../../_core/components/icons";

interface DeleteUserDialogProps {
  open: boolean;
  user: PartnerUser | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteUserDialog = ({ open, user, onConfirm, onCancel }: DeleteUserDialogProps) => {
  const classes = useStyles();

  if (!user) return null;

  return (
    <UIModalDialog
      open={open}
      onClose={onCancel}
      type="warn"
      icon={<WarningIcon />}
      title="Delete User"
      subtitle="This action cannot be undone"
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: "Cancel",
          onClick: onCancel,
          variant: "text"
        },
        {
          label: "Delete Permanently",
          onClick: onConfirm,
          variant: "contained",
          color: "error",
          icon: <WarningIcon />
        }
      ]}
    >
      <UIBox className={classes.content}>
        <UITypography variant="body1" className={classes.message}>
          Are you sure you want to remove this user from the partner portal? This will revoke their partner portal access.
        </UITypography>
        
        <UIBox className={classes.userCard}>
          <UIBox className={classes.userHeader}>
            <UITypography variant="subtitle1" className={classes.userName}>
              {user.name}
            </UITypography>
            <UITypography variant="body2" className={classes.userEmail}>
              {user.email}
            </UITypography>
          </UIBox>
        </UIBox>
      </UIBox>
    </UIModalDialog>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  message: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  userCard: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  userHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  userName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  userEmail: {
    color: theme.palette.text.secondary,
    fontFamily: "monospace",
  },
}));

export default DeleteUserDialog;